<template>
    <div class="bubble-chart" ref="chartContainer"></div>
</template>

<script>
import Vue from "vue";
import * as echarts from "echarts/core";
import { ScatterChart } from "echarts/charts";
import { 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent,
    MarkLineComponent 
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import _ from 'lodash';

echarts.use([
    ScatterChart, 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent, 
    MarkLineComponent,
    CanvasRenderer
]);

export default Vue.extend({
    name: "BubbleChart",
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            default: ""
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        chartOptions() {
            const xAxisField = this.fields.find(f => f._id === _.get(this.settings, 'xAxis.field'));
            const yAxisField = this.fields.find(f => f._id === _.get(this.settings, 'yAxis.field'));
            
            if (!xAxisField || !yAxisField || !this.data.length) return null;

            // Boyut değerlerini normalize et
            const minSize = _.get(this.settings, 'bubble.minSize', 10);
            const maxSize = _.get(this.settings, 'bubble.maxSize', 50);

            const bubbleData = this.data.map(item => ([
                item[xAxisField.name],
                item[yAxisField.name],
                item.size || 1
            ]));

            return {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    formatter: (params) => {
                        return [
                            `${xAxisField.name}: ${params.value[0]}`,
                            `${yAxisField.name}: ${params.value[1]}`,
                            `Boyut: ${params.value[2]}`
                        ].join('<br/>');
                    }
                },
                xAxis: {
                    type: 'value',
                    name: xAxisField.name,
                    nameLocation: 'middle',
                    nameGap: 30
                },
                yAxis: {
                    type: 'value',
                    name: yAxisField.name,
                    nameLocation: 'middle',
                    nameGap: 30
                },
                series: [{
                    type: 'scatter',
                    symbolSize: (data) => {
                        return this.calculateBubbleSize(data[2], minSize, maxSize);
                    },
                    data: bubbleData,
                    itemStyle: {
                        opacity: 0.8
                    },
                    emphasis: {
                        focus: 'series',
                        itemStyle: {
                            opacity: 1
                        }
                    }
                }]
            };
        }
    },
    methods: {
        calculateBubbleSize(value, minSize, maxSize) {
            const allSizes = this.data.map(item => item.size || 1);
            const minValue = Math.min(...allSizes);
            const maxValue = Math.max(...allSizes);
            
            if (minValue === maxValue) return (minSize + maxSize) / 2;
            
            const scale = (value - minValue) / (maxValue - minValue);
            return minSize + scale * (maxSize - minSize);
        },
        initChart() {
            if (!this.$refs.chartContainer) return;
            
            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
        },
        initResizeObserver() {
            this.resizeObserver = new ResizeObserver(_.debounce(() => {
                if (this.chart) {
                    this.chart.resize();
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.chartOptions) return;
            this.chart.setOption(this.chartOptions, true);
            this.chart.resize();
        }
    },
    mounted() {
        this.initChart();
        this.initResizeObserver();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        },
        settings: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.bubble-chart {
    width: 100%;
    height: 100%;
    min-height: 200px;
}
</style> 